// Game sound effects (suunnitelma.md section 32), synthesised with the Web Audio API:
// no audio files, so there are no licensing questions. Short, soft and consistent.

export type SoundName =
  | 'shot'
  | 'splash'
  | 'hit'
  | 'sunk'
  | 'fleetDestroyed'
  | 'turn'
  | 'challenge'
  | 'accepted'
  | 'declined'
  | 'victory'
  | 'defeat'
  | 'connectionLost'
  | 'connectionRestored'
  | 'place'
  | 'cardFlip'
  | 'pairFound'
  | 'shipSelect'
  | 'shipPlace'
  | 'placeInvalid'
  | 'rotate'
  | 'fleetRandom'
  | 'fleetClear'
  | 'fleetReady'
  | 'turnTick'
  | 'bankTick'
  | 'chatMessage'
  | 'chatPrivate'
  | 'diceRoll'
  | 'diceHold'
  | 'diceScore'
  | 'tableJoin'
  | 'snakeEat'
  | 'snakeCrash'
  | 'snakeCount'
  | 'tetraxRotate'
  | 'tetraxLock'
  | 'tetraxClear'
  | 'tetraxQuad'
  | 'tetraxGarbage'
  | 'tetraxOver'
  | 'pokerDeal'
  | 'pokerHold'
  | 'pokerWin'
  | 'pokerBigWin'
  | 'pokerCoin'
  | 'pokerSuspense'
  | 'pokerDoubleWin'
  | 'pokerLose'
  | 'pokerPush'
  | 'chessMove'
  | 'chessCapture'
  | 'chessCheck'
  | 'valeCaught'
  | 'valeBurn'
  | 'paskahousu'
  | 'sadTrombone';

export interface SoundSettings {
  /** Sound effects on/off. */
  enabled: boolean;
  /** 0–1 */
  volume: number;
  /** Chat message sounds (can be muted separately). */
  chatSounds: boolean;
  /** Private message notification while playing or on other pages (default on). */
  pmNotifications: boolean;
  /** Radio on/off (Icecast stream, decision 2026-09-29; only with a music permission). */
  music: boolean;
  /** 0–1 */
  musicVolume: number;
  /** Radio muted (the stream stays connected). */
  musicMuted: boolean;
}

const STORAGE_KEY = 'areena.sound';
const DEFAULTS: SoundSettings = {
  enabled: true,
  volume: 0.6,
  chatSounds: true,
  pmNotifications: true,
  music: true,
  musicVolume: 0.5,
  musicMuted: false,
};
const clamp01 = (v: unknown, fallback: number) => (typeof v === 'number' ? Math.min(1, Math.max(0, v)) : fallback);
let settings: SoundSettings = { ...DEFAULTS };
const listeners = new Set<(s: SoundSettings) => void>();

function readStored(): SoundSettings {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<SoundSettings> | null;
    if (!stored) return { ...DEFAULTS };
    return {
      enabled: stored.enabled ?? DEFAULTS.enabled,
      volume: clamp01(stored.volume, DEFAULTS.volume),
      chatSounds: stored.chatSounds ?? DEFAULTS.chatSounds,
      pmNotifications: stored.pmNotifications ?? DEFAULTS.pmNotifications,
      music: stored.music ?? DEFAULTS.music,
      musicVolume: clamp01(stored.musicVolume, DEFAULTS.musicVolume),
      musicMuted: stored.musicMuted ?? DEFAULTS.musicMuted,
    };
  } catch {
    // Storage unavailable: defaults are used.
    return { ...DEFAULTS };
  }
}
settings = readStored();

// The separate radio window changes the radio volume and mute in the same storage.
addEventListener('storage', (e) => {
  if (e.key !== STORAGE_KEY) return;
  settings = readStored();
  listeners.forEach((l) => l(settings));
});

export function getSoundSettings(): SoundSettings {
  return settings;
}

export function setSoundSettings(next: SoundSettings): void {
  settings = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
  listeners.forEach((l) => l(next));
}

export function onSoundSettings(listener: (s: SoundSettings) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

function audio(): { ctx: AudioContext; out: GainNode } | null {
  if (typeof AudioContext === 'undefined') return null;
  if (!ctx) {
    ctx = new AudioContext();
    master = ctx.createGain();
    master.connect(ctx.destination);
  }
  // iOS may report 'interrupted' as well as 'suspended'.
  if (ctx.state !== 'running') void ctx.resume();
  master!.gain.value = settings.volume * 0.8;
  return { ctx, out: master! };
}

/** Browsers allow audio only after a user gesture; unlock on the first interaction. */
type AudioSessionNavigator = Navigator & { audioSession?: { type: string } };

/**
 * iOS/iPadOS: Web Audio follows the silent mode ("ambient" session) and stays mute there.
 * A "playback" audio session (Safari/WebKit 16.4+) lets game sounds play like other media.
 */
function preferPlaybackSession(): void {
  try {
    const session = (navigator as AudioSessionNavigator).audioSession;
    if (session && session.type !== 'playback') session.type = 'playback';
  } catch {
    // Not supported: nothing to do.
  }
}

/** Short diagnostic of the audio system, shown in the sound settings. */
export function audioStatus(): { context: AudioContextState | 'none' | 'unsupported'; session: string } {
  const session = (navigator as AudioSessionNavigator).audioSession?.type ?? '–';
  if (typeof AudioContext === 'undefined') return { context: 'unsupported', session };
  return { context: ctx ? ctx.state : 'none', session };
}

export function unlockAudio(): void {
  preferPlaybackSession();
  const a = audio();
  if (!a) return;
  // iOS/iPadOS (all browsers use WebKit there) starts audio only after playing something inside
  // a user gesture: a one-sample silent buffer is enough.
  const buffer = a.ctx.createBuffer(1, 1, a.ctx.sampleRate);
  const src = a.ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(a.ctx.destination);
  src.start(0);
  if (a.ctx.state !== 'running') void a.ctx.resume();
}

const UNLOCK_EVENTS = ['touchend', 'click', 'keydown'] as const;

/**
 * Unlocks audio on user gestures that iOS accepts (touchend/click, not pointerdown). Keeps
 * listening until the audio context is running, and again if the system suspends it
 * (e.g. after the tab was in the background).
 */
export function installAudioUnlock(): () => void {
  const handler = () => unlockAudio();
  UNLOCK_EVENTS.forEach((e) => addEventListener(e, handler, { passive: true }));
  return () => UNLOCK_EVENTS.forEach((e) => removeEventListener(e, handler));
}

function tone(
  a: { ctx: AudioContext; out: GainNode },
  freq: number,
  start: number,
  duration: number,
  opts: { type?: OscillatorType; gain?: number; endFreq?: number } = {},
) {
  const osc = a.ctx.createOscillator();
  const g = a.ctx.createGain();
  osc.type = opts.type ?? 'sine';
  const t0 = a.ctx.currentTime + start;
  osc.frequency.setValueAtTime(freq, t0);
  if (opts.endFreq) osc.frequency.exponentialRampToValueAtTime(opts.endFreq, t0 + duration);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(opts.gain ?? 0.3, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(g).connect(a.out);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

function noise(
  a: { ctx: AudioContext; out: GainNode },
  start: number,
  duration: number,
  filter: { type: BiquadFilterType; from: number; to?: number; q?: number },
  gain = 0.4,
) {
  const length = Math.ceil(a.ctx.sampleRate * duration);
  const buffer = a.ctx.createBuffer(1, length, a.ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  const src = a.ctx.createBufferSource();
  src.buffer = buffer;
  const f = a.ctx.createBiquadFilter();
  f.type = filter.type;
  f.Q.value = filter.q ?? 1;
  const t0 = a.ctx.currentTime + start;
  f.frequency.setValueAtTime(filter.from, t0);
  if (filter.to) f.frequency.exponentialRampToValueAtTime(filter.to, t0 + duration);
  const g = a.ctx.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  src.connect(f).connect(g).connect(a.out);
  src.start(t0);
  src.stop(t0 + duration);
}

const SOUNDS: Record<SoundName, (a: { ctx: AudioContext; out: GainNode }) => void> = {
  shot: (a) => noise(a, 0, 0.28, { type: 'bandpass', from: 2400, to: 500, q: 0.8 }, 0.35),
  splash: (a) => {
    noise(a, 0, 0.5, { type: 'lowpass', from: 1800, to: 300 }, 0.45);
    tone(a, 520, 0.05, 0.12, { gain: 0.08, endFreq: 260 });
    tone(a, 440, 0.16, 0.1, { gain: 0.06, endFreq: 220 });
  },
  // Hit: a small explosion – sharper and much shorter than sinking, clearly unlike the splash.
  hit: (a) => {
    noise(a, 0, 0.05, { type: 'highpass', from: 2500 }, 0.5); // snap
    noise(a, 0.005, 0.45, { type: 'bandpass', from: 3000, to: 200, q: 0.7 }, 0.75); // bang
    tone(a, 160, 0, 0.3, { type: 'triangle', gain: 0.55, endFreq: 55 }); // hull impact
    tone(a, 420, 0.02, 0.18, { type: 'square', gain: 0.05, endFreq: 260 }); // metallic clang
    noise(a, 0.18, 0.06, { type: 'bandpass', from: 2200, to: 1200, q: 1.2 }, 0.18); // debris
  },
  // Sinking: an explosion, clearly different from a normal hit (user request 2026-09-27):
  // sharp crack, deep boom, debris crackle and a fading rumble.
  sunk: (a) => {
    noise(a, 0, 0.12, { type: 'highpass', from: 1800 }, 0.55); // crack
    noise(a, 0.01, 1.9, { type: 'lowpass', from: 2200, to: 45 }, 0.95); // boom
    tone(a, 95, 0, 0.9, { type: 'sine', gain: 0.7, endFreq: 28 }); // pressure thump
    tone(a, 60, 0.05, 1.4, { type: 'triangle', gain: 0.35, endFreq: 24 });
    for (const [at, len] of [
      [0.22, 0.08],
      [0.38, 0.06],
      [0.55, 0.1],
      [0.8, 0.07],
    ] as const) {
      noise(a, at, len, { type: 'bandpass', from: 2600, to: 900, q: 1.2 }, 0.22); // debris
    }
    noise(a, 0.6, 1.6, { type: 'lowpass', from: 400, to: 40 }, 0.35); // rumble
  },
  // End of a battleship game, the whole fleet sunk (user request 2026-09-29): grander than
  // sinking – a chain of growing explosions, one huge final blast with a long rumble, and a brass
  // chord swelling under it. ~4.5 s.
  fleetDestroyed: (a) => {
    for (const [at, size] of [
      [0, 0.45],
      [0.32, 0.6],
      [0.62, 0.75],
    ] as const) {
      noise(a, at, 0.1, { type: 'highpass', from: 1800 }, 0.4 * size); // crack
      noise(a, at + 0.01, 0.8, { type: 'lowpass', from: 2000, to: 60 }, 0.8 * size); // boom
      tone(a, 110, at, 0.5, { type: 'sine', gain: 0.6 * size, endFreq: 35 }); // thump
    }
    const big = 1.0;
    noise(a, big, 0.22, { type: 'highpass', from: 1500 }, 0.6); // crack
    noise(a, big + 0.01, 3.2, { type: 'lowpass', from: 3000, to: 30 }, 1.0); // blast
    tone(a, 75, big, 2.4, { type: 'sine', gain: 0.8, endFreq: 18 }); // pressure wave
    tone(a, 48, big + 0.05, 2.8, { type: 'triangle', gain: 0.4, endFreq: 20 });
    for (const at of [1.25, 1.4, 1.62, 1.8, 2.05, 2.3, 2.6]) {
      noise(a, at, 0.07 + Math.random() * 0.06, { type: 'bandpass', from: 2400, to: 800, q: 1.2 }, 0.2); // debris
    }
    noise(a, 1.8, 2.7, { type: 'lowpass', from: 320, to: 30 }, 0.4); // rumble

    // Brass chord (D major) swelling from under the blast, through an opening lowpass filter.
    const t0 = a.ctx.currentTime + big + 0.15;
    const f = a.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(350, t0);
    f.frequency.exponentialRampToValueAtTime(2200, t0 + 1.4);
    f.frequency.exponentialRampToValueAtTime(500, t0 + 3.2);
    const g = a.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.9);
    g.gain.setValueAtTime(0.22, t0 + 2.0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 3.3);
    f.connect(g).connect(a.out);
    for (const freq of [73.4, 110, 146.8, 147.6, 185, 220]) {
      const o = a.ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = freq;
      o.connect(f);
      o.start(t0);
      o.stop(t0 + 3.4);
    }
  },
  turn: (a) => {
    tone(a, 660, 0, 0.18, { gain: 0.2 });
    tone(a, 880, 0.12, 0.25, { gain: 0.2 });
  },
  challenge: (a) => {
    tone(a, 523, 0, 0.18, { type: 'triangle', gain: 0.25 });
    tone(a, 659, 0.12, 0.18, { type: 'triangle', gain: 0.25 });
    tone(a, 784, 0.24, 0.35, { type: 'triangle', gain: 0.25 });
  },
  accepted: (a) => {
    tone(a, 587, 0, 0.15, { gain: 0.22 });
    tone(a, 880, 0.1, 0.3, { gain: 0.22 });
  },
  declined: (a) => {
    tone(a, 440, 0, 0.18, { gain: 0.2 });
    tone(a, 330, 0.14, 0.3, { gain: 0.2 });
  },
  victory: (a) => {
    [523, 659, 784, 1047].forEach((f, i) => tone(a, f, i * 0.12, i === 3 ? 0.7 : 0.2, { type: 'triangle', gain: 0.25 }));
  },
  defeat: (a) => {
    [392, 349, 311, 262].forEach((f, i) => tone(a, f, i * 0.18, i === 3 ? 0.8 : 0.25, { type: 'triangle', gain: 0.22 }));
  },
  connectionLost: (a) => {
    tone(a, 600, 0, 0.15, { gain: 0.18, endFreq: 400 });
    tone(a, 400, 0.16, 0.25, { gain: 0.18, endFreq: 250 });
  },
  connectionRestored: (a) => {
    tone(a, 400, 0, 0.12, { gain: 0.18, endFreq: 600 });
    tone(a, 600, 0.12, 0.2, { gain: 0.18, endFreq: 900 });
  },
  place: (a) => {
    tone(a, 220, 0, 0.09, { type: 'triangle', gain: 0.35, endFreq: 160 });
    noise(a, 0, 0.05, { type: 'highpass', from: 2500 }, 0.15);
  },
  cardFlip: (a) => noise(a, 0, 0.12, { type: 'bandpass', from: 1800, to: 3200, q: 1.5 }, 0.3),
  // Nopparalli (decision 2026-09-27): dice rattling in a cup and tumbling on the table.
  diceRoll: (a) => {
    noise(a, 0, 0.22, { type: 'bandpass', from: 1400, to: 2600, q: 2 }, 0.18); // cup
    for (let i = 0; i < 7; i++) {
      const at = 0.22 + i * 0.055 + (i % 3) * 0.012;
      noise(a, at, 0.035, { type: 'bandpass', from: 2400 - i * 120, q: 4 }, 0.32 - i * 0.03); // clicks
      tone(a, 900 - i * 40, at, 0.03, { type: 'triangle', gain: 0.06 });
    }
  },
  diceHold: (a) => tone(a, 1500, 0, 0.04, { type: 'triangle', gain: 0.12, endFreq: 1200 }),
  diceScore: (a) => {
    tone(a, 660, 0, 0.1, { type: 'triangle', gain: 0.18 });
    tone(a, 990, 0.08, 0.18, { type: 'triangle', gain: 0.18 });
  },
  tableJoin: (a) => {
    tone(a, 494, 0, 0.12, { type: 'triangle', gain: 0.18 });
    tone(a, 740, 0.1, 0.2, { type: 'triangle', gain: 0.18 });
  },
  // Snake (decision 2026-09-30): a square-wave blip like the old phones, a crunch on a crash.
  snakeEat: (a) => tone(a, 1568, 0, 0.06, { type: 'square', gain: 0.07, endFreq: 2093 }),
  snakeCrash: (a) => {
    tone(a, 330, 0, 0.35, { type: 'square', gain: 0.1, endFreq: 70 });
    noise(a, 0, 0.25, { type: 'lowpass', from: 1800, to: 200 }, 0.25);
  },
  snakeCount: (a) => tone(a, 988, 0, 0.1, { type: 'square', gain: 0.06 }),
  // Tetrax (2026-10-03): short square-wave blips of the old handhelds; a rising run for lines.
  tetraxRotate: (a) => tone(a, 880, 0, 0.035, { type: 'square', gain: 0.04 }),
  tetraxLock: (a) => tone(a, 196, 0, 0.06, { type: 'square', gain: 0.07, endFreq: 150 }),
  tetraxClear: (a) => {
    tone(a, 784, 0, 0.07, { type: 'square', gain: 0.07 });
    tone(a, 1047, 0.07, 0.1, { type: 'square', gain: 0.07 });
  },
  tetraxQuad: (a) => {
    [523, 659, 784, 1047].forEach((f, i) => tone(a, f, i * 0.07, 0.1, { type: 'square', gain: 0.08 }));
    tone(a, 1568, 0.28, 0.25, { type: 'square', gain: 0.07 });
  },
  tetraxGarbage: (a) => {
    tone(a, 160, 0, 0.18, { type: 'sawtooth', gain: 0.08, endFreq: 90 });
    noise(a, 0, 0.12, { type: 'lowpass', from: 900, to: 200 }, 0.15);
  },
  tetraxOver: (a) => [494, 440, 392, 330, 262].forEach((f, i) => tone(a, f, i * 0.12, 0.14, { type: 'square', gain: 0.07 })),
  // Pokeriautomaatti (2026-10-03): the bright beeps of the 80s machine – a tick per card, a blip for a hold,
  // a jingle for a win, a coin tick while the win is counted, a drum roll before the doubling card.
  pokerDeal: (a) => {
    noise(a, 0, 0.03, { type: 'highpass', from: 2500 }, 0.18);
    tone(a, 1320, 0, 0.03, { type: 'square', gain: 0.035 });
  },
  pokerHold: (a) => tone(a, 1760, 0, 0.05, { type: 'square', gain: 0.05, endFreq: 2093 }),
  pokerWin: (a) => [784, 988, 1175, 1568].forEach((f, i) => tone(a, f, i * 0.075, 0.09, { type: 'square', gain: 0.06 })),
  pokerBigWin: (a) => {
    [523, 659, 784, 1047, 784, 1047, 1319, 1568].forEach((f, i) => tone(a, f, i * 0.09, 0.11, { type: 'square', gain: 0.07 }));
    tone(a, 2093, 0.75, 0.4, { type: 'triangle', gain: 0.1 });
  },
  pokerCoin: (a) => tone(a, 2637, 0, 0.025, { type: 'square', gain: 0.035 }),
  pokerSuspense: (a) => {
    for (let i = 0; i < 8; i++) noise(a, i * 0.06, 0.04, { type: 'bandpass', from: 300 + i * 40, q: 2 }, 0.2);
  },
  pokerDoubleWin: (a) => {
    tone(a, 1047, 0, 0.1, { type: 'square', gain: 0.07 });
    tone(a, 1568, 0.1, 0.2, { type: 'square', gain: 0.07 });
  },
  pokerLose: (a) => tone(a, 220, 0, 0.5, { type: 'sawtooth', gain: 0.08, endFreq: 70 }),
  pokerPush: (a) => tone(a, 660, 0, 0.18, { type: 'triangle', gain: 0.12 }),
  // Chess (decision 2026-09-30): a wooden knock for a move, a double knock for a capture, a bell for check.
  chessMove: (a) => {
    noise(a, 0, 0.05, { type: 'bandpass', from: 900, q: 3 }, 0.35);
    tone(a, 220, 0, 0.07, { type: 'triangle', gain: 0.18, endFreq: 160 });
  },
  chessCapture: (a) => {
    noise(a, 0, 0.05, { type: 'bandpass', from: 1100, q: 3 }, 0.35);
    noise(a, 0.07, 0.06, { type: 'bandpass', from: 750, q: 3 }, 0.4);
    tone(a, 180, 0.07, 0.09, { type: 'triangle', gain: 0.2, endFreq: 120 });
  },
  // Valepaskahousu (decision 2026-09-30): a caught lie and a burning pile.
  valeCaught: (a) => {
    tone(a, 660, 0, 0.12, { type: 'square', gain: 0.1 });
    tone(a, 440, 0.12, 0.12, { type: 'square', gain: 0.1 });
    tone(a, 330, 0.24, 0.25, { type: 'square', gain: 0.1, endFreq: 220 });
  },
  valeBurn: (a) => noise(a, 0, 0.6, { type: 'bandpass', from: 400, to: 2400, q: 0.8 }, 0.3),
  // The paskahousu is announced with a cheeky circus march (user request 2026-09-30) ...
  paskahousu: (a) => {
    const beat = 0.16;
    const melody: [number, number, number][] = [
      // [frequency, start beat, length in beats]
      [392, 0, 1], [523, 1, 1], [659, 2, 1], [784, 3, 2],
      [659, 5, 1], [784, 6, 3],
      [698, 9.5, 1], [659, 10.5, 1], [587, 11.5, 1], [523, 12.5, 3],
    ];
    for (const [f, at, len] of melody) tone(a, f, at * beat, len * beat * 0.9, { type: 'square', gain: 0.08 });
    // oom-pah bass
    for (let i = 0; i < 8; i++) tone(a, i % 2 ? 196 : 131, i * 2 * beat, beat * 0.8, { type: 'triangle', gain: 0.18 });
    // a final raspberry
    tone(a, 110, 16 * beat, 0.5, { type: 'sawtooth', gain: 0.12, endFreq: 70 });
    noise(a, 16 * beat, 0.45, { type: 'lowpass', from: 500, to: 150 }, 0.2);
  },
  // ... and the paskahousu themselves hears a sad trombone: three falling notes and a wobbling last one.
  sadTrombone: (a) => {
    tone(a, 294, 0, 0.36, { type: 'sawtooth', gain: 0.1 });
    tone(a, 277, 0.4, 0.36, { type: 'sawtooth', gain: 0.1 });
    tone(a, 262, 0.8, 0.36, { type: 'sawtooth', gain: 0.1 });
    for (let i = 0; i < 10; i++) tone(a, i % 2 ? 240 : 252, 1.2 + i * 0.11, 0.12, { type: 'sawtooth', gain: 0.1 - i * 0.007 });
  },
  chessCheck: (a) => {
    tone(a, 1318, 0, 0.35, { type: 'sine', gain: 0.14 });
    tone(a, 1976, 0, 0.25, { type: 'sine', gain: 0.06 });
  },
  pairFound: (a) => {
    tone(a, 784, 0, 0.14, { gain: 0.2 });
    tone(a, 1175, 0.1, 0.3, { gain: 0.2 });
  },

  // Fleet placement (user request 2026-09-27).
  shipSelect: (a) => tone(a, 1200, 0, 0.05, { type: 'triangle', gain: 0.15, endFreq: 900 }),
  shipPlace: (a) => {
    tone(a, 180, 0, 0.12, { type: 'triangle', gain: 0.4, endFreq: 110 }); // metallic clunk
    noise(a, 0, 0.04, { type: 'bandpass', from: 1500, q: 2 }, 0.2);
    noise(a, 0.05, 0.2, { type: 'lowpass', from: 900, to: 250 }, 0.2); // small splash
  },
  placeInvalid: (a) => tone(a, 110, 0, 0.16, { type: 'sawtooth', gain: 0.08, endFreq: 95 }),
  rotate: (a) => noise(a, 0, 0.14, { type: 'bandpass', from: 700, to: 2600, q: 1.5 }, 0.22),
  fleetRandom: (a) => {
    noise(a, 0, 0.3, { type: 'bandpass', from: 600, to: 2400, q: 1 }, 0.15);
    for (let i = 0; i < 10; i++) {
      tone(a, 200 - i * 6, 0.06 + i * 0.05, 0.08, { type: 'triangle', gain: 0.25, endFreq: 120 });
    }
  },
  // Countdown (user request 2026-09-27): soft beeps at the end of the turn time, a sharper
  // double beep every second while the time bank runs.
  turnTick: (a) => tone(a, 880, 0, 0.09, { type: 'triangle', gain: 0.22 }),
  // Chat: a soft blip for lobby/match messages, a two-note chime for private messages.
  chatMessage: (a) => tone(a, 1046, 0, 0.08, { type: 'sine', gain: 0.12, endFreq: 1318 }),
  chatPrivate: (a) => {
    tone(a, 988, 0, 0.1, { type: 'triangle', gain: 0.16 });
    tone(a, 1480, 0.1, 0.16, { type: 'triangle', gain: 0.16 });
  },
  bankTick: (a) => {
    tone(a, 1320, 0, 0.05, { type: 'triangle', gain: 0.12 });
    tone(a, 1320, 0.08, 0.05, { type: 'triangle', gain: 0.1 });
  },
  fleetClear: (a) => noise(a, 0, 0.35, { type: 'bandpass', from: 2600, to: 400, q: 1.2 }, 0.22),
  fleetReady: (a) => {
    // Short ship horn: two detuned low tones through a lowpass filter.
    const t0 = a.ctx.currentTime;
    const f = a.ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 900;
    const g = a.ctx.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.28, t0 + 0.08);
    g.gain.setValueAtTime(0.28, t0 + 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.9);
    f.connect(g).connect(a.out);
    for (const freq of [110, 110.8, 165]) {
      const o = a.ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = freq;
      o.connect(f);
      o.start(t0);
      o.stop(t0 + 0.95);
    }
  },
};

/** Chat sounds follow both the sound effect switch and the separate chat sound switch. */
export function playChatSound(name: 'chatMessage' | 'chatPrivate'): void {
  if (settings.chatSounds) playSound(name);
}

export function playSound(name: SoundName, delayMs = 0): void {
  if (!settings.enabled || settings.volume <= 0) return;
  const run = () => {
    try {
      const a = audio();
      if (a) SOUNDS[name](a);
    } catch {
      // Audio is optional.
    }
  };
  if (delayMs > 0) setTimeout(run, delayMs);
  else run();
}
